import * as React from 'react'
import { InvokeCreator } from 'xstate'

import {
  TFProductDetail,
  TQCartItem,
  TQCartItemVariables,
  TWarePriceTypeEnum,
  useQCartItemLazyQuery,
  useQProductDetailLazyQuery,
} from '~graphql/generated/clientGraphql'
import { TQProductDetailVariables } from '~graphql/generated/graphql'
import {
  TProductDetailMachineTypes,
  TProductDetailState,
} from '~logic/xstate/machines/menu/productDetailMachineTypes'

import { useProductDetailCtx } from '../contexts/ProductDetailProvider'

type TProductDetailMachineService = InvokeCreator<
  TProductDetailMachineTypes['context'],
  TProductDetailMachineTypes['events']
>

type TProps = {
  branchId: ID
  warePriceType?: TWarePriceTypeEnum
}

/**
 * returns a function for fetching data for ProductDetail,
 * to be passed to the useInterpret/useMachine hook as a service,
 * fetches either more detailed info about product in list (when adding a new product)
 * or a cart item info (when that item is being updated)
 * and transforms in into a unified shaped used by the productDetailMachine
 */
export const useLoadProductDetailService = ({ branchId, warePriceType }: TProps) => {
  // TODO add context to lazy query after moving localeProvider to shared

  const [qCartItem] = useQCartItemLazyQuery()
  const [qProductDetail] = useQProductDetailLazyQuery()

  const { product } = useProductDetailCtx()
  const isCombinationMeal = Boolean(product?.isCombinationMeal)

  // fetch either product detail or cart item & shape it into TProductDetailState
  const fetchProductDetail = React.useCallback<TProductDetailMachineService>(
    async (context) => {
      if (!context.productId) {
        return null
      }

      // editing cart item
      if (context.editedItemIds !== null) {
        if (context.editedItemIds.length === 0) {
          throw Error(`product not found`)
        }

        const qCartItemVariables: TQCartItemVariables = {
          itemId: context.editedItemIds[0],
          priceType: warePriceType,
          isCombinationMeal,
        }
        const { data, error } = await qCartItem({ variables: qCartItemVariables })

        if (!data || error) {
          // throws an error (instead of the usual return) because this code is used as a xstate machine service
          // when invoked service throws, we can react to it in the machine config (onError transition)
          throw Error(error?.message)
        }

        return getStateFromCartItem(data.item, context.editedItemIds.length)
      }

      const qProductDetailVariables: TQProductDetailVariables = {
        productId: context.productId,
        priceType: warePriceType,
        branchId,
        isCombinationMeal,
      }
      const { data, error } = await qProductDetail({ variables: qProductDetailVariables })

      if (!data || error) {
        // throws an error (instead of the usual return) because this code is used as a xstate machine service
        // when invoked service throws, we can react to it in the machine config (onError transition)
        throw Error(error?.message)
      }

      return getStateFromProduct(data.product)
    },
    [branchId, qCartItem, isCombinationMeal, qProductDetail, warePriceType],
  )

  return { fetchProductDetail }
}

// * HELPERS

type TInitState = {
  note?: string
  amount?: number
  uniqueAddonCounts?: Map<string, number>
}

// shape loaded product detail into TProductDetailState
// also accepts some custom initial state
const getStateFromProduct = (
  product: TFProductDetail,
  initState?: TInitState,
): TProductDetailState => {
  const note = initState?.note ?? ``
  const amount = initState?.amount ?? 1

  const addonCategories = product.addonCategories.map((category) => {
    const addons = category.addons.map((addon) => {
      const amount = initState?.uniqueAddonCounts?.get(addon.id) ?? 0

      return { ...addon, amount }
    })

    return { ...category, addons }
  })

  return { ...product, note, amount, addonCategories }
}

// shape loaded cart item into TProductDetailState
// setting the initial state according to the cart item’s current state
const getStateFromCartItem = (
  cartItem: TQCartItem['item'],
  itemCount: number,
): TProductDetailState => {
  const amount = itemCount
  const note = cartItem.note ?? ``
  const uniqueAddonCounts = getUniqueAddonCounts(cartItem)

  return getStateFromProduct(cartItem.product, { note, amount, uniqueAddonCounts })
}

// maps cart item’s addons to an object where each addon product is represented once (as a key)
// and number of appearances of that product is stored as a value
const getUniqueAddonCounts = (cartItem: TQCartItem['item']) => {
  const uniqueAddonCounts = new Map<ID, number>()

  cartItem.selectedAddons.forEach((addon) => {
    const prevCount = uniqueAddonCounts.get(addon.product.id) ?? 0
    uniqueAddonCounts.set(addon.product.id, prevCount + 1)
  })

  return uniqueAddonCounts
}
